import type { User } from "firebase/auth";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { ArrowLeft, LoaderCircle, ShieldCheck, UserRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { friendlyError } from "../lib/api";
import { auth } from "../lib/firebase";
import { preloadRoute } from "../lib/routePreload";
import { getQuestForHostPath } from "../data/quests";
import { JourneyShell, SiteTopbar } from "./JourneyShell";
import "./HostAccessScreen.css";

export function HostAccessScreen({ user, loading = false }: { user?: User | null; loading?: boolean } = {}) {
  return <AccountAccessScreen user={user} loading={loading} hosting />;
}

export function AccountAccessScreen({ user, loading = false, hosting = false }: { user?: User | null; loading?: boolean; hosting?: boolean } = {}) {
  const location = useLocation();
  const quest = getQuestForHostPath(location.pathname);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const signInPending = useRef(false);
  const mounted = useRef(true);
  const localEmulator = ["localhost", "127.0.0.1"].includes(window.location.hostname);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  async function googleSignIn() {
    if (loading || signInPending.current) return;
    signInPending.current = true;
    setBusy(true);
    preloadRoute(hosting ? location.pathname : "/account");
    setError("");
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (caught) {
      if (mounted.current) setError(friendlyError(caught));
    } finally {
      signInPending.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  return (
    <JourneyShell user={user}>
      <section className="join-card hub-join-card host-access-card" aria-labelledby="host-access-title">
        <div className="host-access-heading">
          <div><div className="card-kicker">{hosting ? quest ? `${quest.title} · Host access` : "Host access" : "Your SideQuest account"}</div><h2 id="host-access-title">{hosting ? <>Start a little<br />team adventure.</> : <>A place for you<br />and your people.</>}</h2></div>
          <div className="host-access-icon" aria-hidden="true">{hosting ? <ShieldCheck size={27} /> : <UserRound size={27} />}</div>
        </div>
        <p>{hosting ? quest ? `Sign in to host ${quest.title}. Your selected quest will open as soon as you’re signed in.` : "Sign in to create a room and keep your trivia sets and scheduled draws together." : "Log in to create your player profile and choose your avatar. You’re welcome here whether you play, host, or both."}</p>
        {error && <div className="form-error" role="alert">{error}</div>}
        <button type="button" className="google-button" onClick={() => void googleSignIn()} disabled={loading || busy} aria-busy={loading || busy}>
          {loading || busy ? <LoaderCircle className="spin" size={19} aria-hidden="true" /> : <strong aria-hidden="true">G</strong>}
          {loading ? "Checking sign-in…" : busy ? "Signing in…" : "Continue with Google"}
        </button>
        <p className="host-access-note">Player accounts are optional. You can also join a room as a guest with a room code.</p>
        {localEmulator && <small className="host-access-emulator">In the local emulator, choose or create a test Google account.</small>}
        <Link to={quest ? `/?quest=${quest.id}` : "/"} className="host-access-back"><ArrowLeft size={15} /> {quest ? "Back to quest" : "Back to player join"}</Link>
      </section>
    </JourneyShell>
  );
}

export function HostTopbar({ user }: { user: User }) {
  return (
    <div className="journey-home host-site-nav">
      <SiteTopbar user={user} />
    </div>
  );
}
